import { z } from 'zod'
import { metadatosArchivos } from './publicaciones'

// Zod 4 compila los objetos con `new Function`, que la CSP del portal bloquea.
z.config({ jitless: true })

/**
 * Contratos del panel de publicación y el flujo de revisión (C3 · §7).
 */

export const TOPE_COMENTARIO = 2000

export const comentarioEntrada = z.object({
  texto: z
    .string({ error: 'Escribí un comentario.' })
    .trim()
    .min(1, 'Escribí un comentario.')
    .max(TOPE_COMENTARIO, `El comentario: hasta ${String(TOPE_COMENTARIO)} caracteres.`),
  /** Carrusel: sobre qué lámina es el comentario (§8.4). */
  laminaNro: z.number().int().min(1).max(20).nullable().default(null),
})
export type ComentarioEntrada = z.input<typeof comentarioEntrada>

/** §7.3 — una versión nueva lleva copy y archivos; el resto no versiona. */
export const versionEntrada = z.object({
  copy: z.string().max(2200, 'El copy: hasta 2.200 caracteres.').default(''),
  archivos: z.array(z.string().min(10)).default([]),
  portada: z
    .string()
    .trim()
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .default(null),
  /** RASTRO decide si además la envía (desde `cambios` queda aprobada). */
  enviar: z.boolean().default(false),
  /** Lo que informó Drive de cada archivo elegido con el selector (Fase 6). */
  metadatos: metadatosArchivos,
})
export type VersionEntrada = z.input<typeof versionEntrada>

/* ── Respuestas ─────────────────────────────────────────── */

export interface AutorComentario {
  id: string
  nombre: string
  tipo: 'cliente' | 'rastro'
}

export interface Comentario {
  id: string
  autor: AutorComentario
  texto: string
  laminaNro: number | null
  /** Número de versión sobre la que se comentó. */
  versionNumero: number
  resuelto: boolean
  creadoEn: string
}

export const ACCIONES = [
  'cargo',
  'envio_a_revision',
  'comento',
  'pidio_cambios',
  'aprobo',
  'subio_version',
  'aprobo_automatica',
  'reprogramo',
  'dio_de_baja',
  'restauro',
] as const
export type AccionActividad = (typeof ACCIONES)[number]

export interface Actividad {
  id: string
  accion: AccionActividad
  /** Nulo en las acciones del sistema, como la aprobación automática. */
  autor: { id: string; nombre: string } | null
  detalle: string | null
  creadoEn: string
}

export interface VersionResumen {
  numero: number
  creadaEn: string
  enviadaEn: string | null
  creadaPor: string
}

/** Lo que muestra el panel: §8.4 y §7.5. */
export interface PanelPublicacion {
  id: string
  proyectoId: string
  titulo: string
  tipo: 'imagen' | 'carrusel' | 'video'
  estado: 'borrador' | 'para_revisar' | 'cambios' | 'aprobada'
  fechaPautada: string
  /** "HH:MM" o null: sin hora (DEC-20). */
  horaPautada: string | null
  /** Feed o historia (DEC-21). */
  destino: 'feed' | 'historia'
  dadaDeBaja: boolean
  /** La versión que se está mirando (la actual salvo que se pida otra). */
  versionNumero: number
  versionActual: number
  copy: string
  archivos: {
    id: string
    rol: 'principal' | 'lamina' | 'portada'
    orden: number
    driveFileId: string
    blurhash: string | null
    miniatura: string | null
    mime: string
  }[]
  pilares: { id: string; nombre: string; color: number }[]
  redes: string[]
  versiones: VersionResumen[]
  comentarios: Comentario[]
  actividad: Actividad[]
  /** "Cargada por RASTRO el 3 oct" / "Aprobada por Valentina el 10 oct". */
  contexto: string
  cliente: { nombre: string; usuarioInstagram: string }
  /** El cliente ya comentó en esta ronda: recién ahí puede pedir cambios. */
  comentoEnEstaRonda: boolean
  /** Piezas que le quedan por revisar, para "Siguiente para revisar". */
  siguienteParaRevisar: string | null
}

/** §7.5 — la franja del panel, según estado y quién mira. */
export function franjaDelPanel(
  estado: PanelPublicacion['estado'],
  esRastro: boolean,
): string | null {
  if (esRastro) {
    if (estado === 'borrador') return 'Borrador. El cliente todavía no la ve.'
    if (estado === 'para_revisar') return 'Esperando la revisión del cliente.'
    if (estado === 'cambios')
      return 'El cliente pidió cambios. Al enviar la versión 2, la publicación queda aprobada.'
    return null
  }
  if (estado === 'para_revisar')
    return 'Comentá y pedí cambios, o aprobá. Tenés una sola revisión por publicación.'
  if (estado === 'cambios')
    return 'RASTRO está trabajando tus cambios. Cuando suba la versión corregida, queda aprobada.'
  return null
}

export const SIN_COMENTARIO = 'Dejá al menos un comentario para pedir cambios.'
export const DESCARTAR_COMENTARIO = 'Tenés un comentario sin enviar. ¿Lo descartás?'
export const TERMINASTE = 'Terminaste. No te queda nada por revisar.'
export const TODO_APROBADO =
  'Todo aprobado. Cuando haya contenido nuevo lo vas a ver acá.'
/**
 * Aviso de contenido que no se ve. En un carrusel dice qué láminas: así
 * quien carga sabe cuál reemplazar y quien revisa sabe que el resto sí se ve.
 * `laminas` cuenta desde 1; un 0 es la pieza entera (imagen o video).
 *
 * Al cliente se le pide que avise a RASTRO. A RASTRO no (OBS-014, DEC-09): el
 * contenido lo muestra Drive con la cuenta de Google abierta en el navegador,
 * así que se le dice que esa cuenta no tiene permiso en la carpeta.
 */
export function contenidoNoVisible(
  laminas: number[],
  { paraRastro = false }: { paraRastro?: boolean } = {},
): string {
  const cuales = laminas.filter((n) => n > 0)
  const lista =
    cuales.length === 0
      ? 'esta pieza'
      : cuales.length === 1
        ? `la lámina ${String(cuales[0])}`
        : `las láminas ${cuales.slice(0, -1).join(', ')} y ${String(cuales.at(-1))}`
  const salida = paraRastro
    ? 'La cuenta de Google abierta en este navegador no tiene permiso en la carpeta del cliente en Drive.'
    : 'Avisale a tu contacto en RASTRO.'
  return `No podemos mostrarte ${lista}. ${salida}`
}
